import {buildGPX, GarminBuilder} from "gpx-builder";
import DateTimeUtil from "utils/DateTimeUtil.js";
import {SETTING_DISTANCE_UNITS, SETTING_SPEED_UNITS, WATERSPEED_TERMS_LINK} from "constants/AppConstants.js";
import {parseGPX, parseGPXWithCustomParser} from "@we-gold/gpxjs";
import {Activity, ACTIVITY_DEFAULT_VALUES, calculateBestAvgSpeed, getFeelingId} from "services/ActivityService.js";
import CoordinateUtil from "utils/CoordinateUtil.js";
import DirectionUtil from "utils/DirectionUtil.js";
import {calculateTimeAndDistanceOfPoints, Point} from "services/PointService.js";
import ConvertUtil from "utils/ConvertUtil.js";
import IdGeneratorUtil from "utils/IdGeneratorUtil.js";
import isNil from "lodash/isNil.js";
import {DOMParser} from "xmldom-qsa";
import {DbCollectionFactory} from "factories/DbCollectionFactory.js";
import {DB_COLLECTION_NAMES} from "constants/AppConstants.js";
import {interpolateAndFilterSpeedFromPoints} from "utils/SpeedUtil.js";
import GpxTrackPoint from "services/GpxTrackPoint.js";
import {getGpxFileName, getGpxTitle} from "services/GpxTitle.js";

const equipmentFactory: any = DbCollectionFactory.create(DB_COLLECTION_NAMES.equip);

let Filesystem: any;
let Directory: any;
let Share: any;
let isAndroid: any;
let functions: any;
let httpsCallableCustom: any;
let Network: any;
if (typeof process.env.REACT_APP_FIREBASE_API_KEY !== "undefined") {
  // @ts-ignore — "/src/FirebaseUtils.js" is a web-app absolute path resolved by the
  // bundler at runtime; it is not resolvable by the node-side TypeScript program.
  const module = await import("/src/FirebaseUtils.js");
  isAndroid = module.isAndroid;
  functions = module.functions;
  httpsCallableCustom = module.httpsCallableCustom;

  const FilesystemModule = await import("@capacitor/filesystem");
  Filesystem = FilesystemModule.Filesystem;
  Directory = FilesystemModule.Directory;

  const ShareModule = await import("@capacitor/share");
  Share = ShareModule.Share;

  const NetworkModule = await import("@capacitor/network");
  Network = NetworkModule.Network;
}

export function generateGPX(activity: any, forGP3S: boolean = false): string | null {
  if (activity?.points?.length) {
    const gpxData = new GarminBuilder();
    const Metadata = GarminBuilder.MODELS.Metadata;
    const Copyright = GarminBuilder.MODELS.Copyright;
    const Author = GarminBuilder.MODELS.Person;
    const Link = GarminBuilder.MODELS.Link;

    const gpxPoints: any[] = [];
    let lastGPXPoint: any = null;
    activity.points.forEach((point: any, index: number, points: any[]) => {
      // We need to ensure that each point for GP3S should be at most 2 seconds distants, otherwise the GP3S will not be able to read the file
      // If it is not, we need to add new points in between to ensure that the distance is at most 2 seconds
      // We also check if the point is the same as the previous one (timestamp or coordinates), if it is, we don't add it
      if (forGP3S && index > 0) {
        const previousPoint = points[index - 1];

        if (
          lastGPXPoint &&
          (point.unixTimestamp === lastGPXPoint.unixTimestamp ||
            (point.latitude === lastGPXPoint.lat && point.longitude === lastGPXPoint.lon))
        ) {
          return;
        }

        const timeDifference = point.unixTimestamp - previousPoint.unixTimestamp;
        if (timeDifference > 2) {
          const numberOfPointsToAdd = Math.floor(timeDifference / 2);
          const timeDifferenceBetweenTwoPoints = timeDifference / (numberOfPointsToAdd + 1);
          for (let i = 1; i <= numberOfPointsToAdd; i++) {
            // We need to add new points in between
            const latitude =
              previousPoint.latitude + (point.latitude - previousPoint.latitude) * (i / (numberOfPointsToAdd + 1));
            const longitude =
              previousPoint.longitude + (point.longitude - previousPoint.longitude) * (i / (numberOfPointsToAdd + 1));
            gpxPoints.push(
              new GpxTrackPoint(latitude, longitude, {
                time: DateTimeUtil.getDateByTimeStamp(previousPoint.unixTimestamp + timeDifferenceBetweenTwoPoints * i),
                ele:
                  isNaN(previousPoint.realElevationInMeter) ||
                  previousPoint.realElevationInMeter < 0 ||
                  isNaN(point.realElevationInMeter) ||
                  point.realElevationInMeter < 0
                    ? undefined
                    : previousPoint.elevationInMeter +
                      (point.elevationInMeter - previousPoint.elevationInMeter) * (i / (numberOfPointsToAdd + 1)),
                hr:
                  previousPoint.heartRate && point.heartRate
                    ? previousPoint.heartRate +
                      (point.heartRate - previousPoint.heartRate) * (i / (numberOfPointsToAdd + 1))
                    : undefined,
                speed:
                  previousPoint.speedInMPS +
                  (point.speedInMPS - previousPoint.speedInMPS) * (i / (numberOfPointsToAdd + 1)),
                course:
                  previousPoint.bearing > 0 && previousPoint.bearing < 360 && point.bearing > 0 && point.bearing < 360
                    ? (previousPoint.bearing +
                        (point.bearing - previousPoint.bearing) * (i / (numberOfPointsToAdd + 1))) %
                      360
                    : undefined,
                desc: "Added by Waterspeed to ensure at most 2 seconds distance between points"
              })
            );
          }
        }
      }

      gpxPoints.push(
        new GpxTrackPoint(point.latitude, point.longitude, {
          time: DateTimeUtil.getDateByTimeStamp(point.unixTimestamp),
          ele: isNaN(point.realElevationInMeter) || point.realElevationInMeter < 0 ? undefined : point.elevationInMeter,
          hr: point.heartRate ? point.heartRate : undefined,
          speed: point.speedInMPS >= 0 ? point.speedInMPS : undefined,
          course: point.bearing > 0 && point.bearing < 360 ? point.bearing : undefined
        })
      );

      lastGPXPoint = point;
    });

    if (gpxPoints) {
      const sportName = activity.waterSportName;
      const link = new Link("https://waterspeedapp.com", {text: "Waterspeed", type: "text/html"});
      gpxData.setMetadata(
        new Metadata({
          name: getGpxTitle(activity),
          desc: `Waterspeed ${sportName ? `${sportName} ` : ""}activity ${activity.id} on ${
            activity.dateInYYYYMMDDTHHMMSSZ
          }`,
          author: new Author({
            name: "Waterspeed",
            link: link,
            email: "support@waterspeedapp.com"
          }),
          copyright: new Copyright("Waterspeed Technologies Limited", {
            year: 2023,
            license: WATERSPEED_TERMS_LINK
          }),
          time: new Date()
        })
      );
      gpxData.setTracks([
        new GarminBuilder.MODELS.Track([new GarminBuilder.MODELS.Segment(gpxPoints)], {
          name: getGpxTitle(activity),
          desc: `Waterspeed activity ${activity.id}`,
          src: "Waterspeed",
          type: sportName || undefined
        })
      ]);

      return buildGPX(gpxData.toObject());
    }
  }
  return null;
}

async function createGPXFile(activity: any): Promise<void> {
  if (activity) {
    const gpxData = generateGPX(activity);

    if (gpxData) {
      const fileName = getGpxFileName(activity);
      if (isAndroid) {
        await Filesystem.writeFile({
          path: fileName,
          data: gpxData,
          directory: Directory.Cache,
          encoding: "utf8"
        });

        const {uri} = await Filesystem.getUri({
          path: fileName,
          directory: Directory.Cache
        });

        await Share.share({
          files: [uri],
          title: fileName,
          dialogTitle: "Share GPX file"
        });
      } else {
        const url = "data:application/gpx+xml;charset=utf-8," + encodeURIComponent(gpxData);
        const link = document.createElement("a");

        link.download = fileName;
        link.href = url;
        document.body.appendChild(link);
        link.click();
      }
    }
  }
}

export const importGpxFileToActivityDb = async (
  gpxData: string,
  userUid: string,
  convert: boolean = true,
  useCustomDomParser: boolean = false,
  forcedWatersportId?: number
): Promise<any> => {
  let data: any;
  let error: any;
  if (useCustomDomParser) {
    const customParseMethod = (txt: string) => {
      return new DOMParser().parseFromString(txt, "text/xml");
    };

    [data, error] = parseGPXWithCustomParser(gpxData, customParseMethod);
  } else {
    [data, error] = parseGPX(gpxData);
  }

  if (error) {
    throw new Error("Error parsing GPX file: " + error);
  }

  if (!data || !data.tracks || data.tracks.length === 0) {
    throw new Error("No tracks found in the parsed GPX");
  }

  const track = data.tracks[0];
  if (!track || !track.points || track.points.length === 0) {
    throw new Error("No track points found in the parsed GPX");
  }

  // filter out invalid points
  track.points = track.points.filter(
    (point: any) => !isNil(point.latitude) && !isNil(point.longitude) && !isNil(point.time)
  );

  // Deduplicate points based on timestamp, latitude, and longitude
  // Keep the point with more data (e.g., with course/bearing if available)
  const originalCount = track.points.length;
  const uniquePoints: any[] = [];
  const seenKeys = new Set<string>();

  track.points.forEach((point: any) => {
    const key = `${point.time.getTime()}-${point.latitude.toFixed(7)}-${point.longitude.toFixed(7)}`;

    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniquePoints.push(point);
    } else {
      // Found duplicate - check if this one has more data (e.g., course/bearing)
      const existingIndex = uniquePoints.findIndex(
        (p: any) =>
          p.time.getTime() === point.time.getTime() &&
          Math.abs(p.latitude - point.latitude) < 0.0000001 &&
          Math.abs(p.longitude - point.longitude) < 0.0000001
      );

      if (existingIndex >= 0) {
        const existing = uniquePoints[existingIndex];
        // Mirror the speed/course/hr fallback chain from the main
        // mapper below: some exports nest `gpxdata:course` inside the
        // Garmin TrackPointExtension wrapper.
        const courseOf = (p: any) =>
          p.extensions?.course ||
          p.extensions?.["gpxdata:course"] ||
          p.extensions?.["gpxtpx:TrackPointExtension"]?.["gpxtpx:course"] ||
          p.extensions?.["gpxtpx:TrackPointExtension"]?.["gpxdata:course"];

        // If new point has course and existing doesn't, replace it
        if (courseOf(point) && !courseOf(existing)) {
          uniquePoints[existingIndex] = point;
        }
      }
    }
  });

  track.points = uniquePoints;
  if (originalCount !== track.points.length) {
    console.log(
      `Deduplicated GPX points: ${track.points.length} unique from ${originalCount} (removed ${originalCount - track.points.length} duplicates)`
    );
  }

  // Prepare points for speed interpolation.
  //
  // Speed/course/hr can land in any of three places depending on the
  // exporting app and the GPX 1.1 extension namespace it picked:
  //
  //   1. Bare top-level keys (`extensions.speed`)            — rare
  //   2. Top-level namespaced keys (`extensions["gpxdata:speed"]`)
  //      — older Suunto / generic GPX 1.1
  //   3. Nested inside the Garmin TrackPointExtension:
  //        extensions["gpxtpx:TrackPointExtension"]["gpxtpx:speed"]
  //      — Garmin/Strava/Suunto modern, and most Waterspeed exports
  //
  // Some Waterspeed exports (e.g. `doc/vmg/GPX/Waterspeed 2025-12-26
  // 14.25.31.gpx`) mix the two child namespaces — they wrap the data
  // in `gpxtpx:TrackPointExtension` but write the speed as
  // `gpxdata:speed`. Without checking that combination every speed
  // resolves to -1, all points fall below the riding threshold, and
  // the recognizer rejects every maneuver in the file ("you don't
  // recognize anything at all").
  const tpx = (pt: any) => pt.extensions?.["gpxtpx:TrackPointExtension"];
  const gpxPoints = track.points.map((pt: any, index: number) => ({
    latitude: pt.latitude,
    longitude: pt.longitude,
    timestamp: pt.time.getTime() / 1000,
    speed:
      pt.extensions?.speed ||
      pt.extensions?.["gpxdata:speed"] ||
      tpx(pt)?.["gpxtpx:speed"] ||
      tpx(pt)?.["gpxdata:speed"] ||
      -1,
    heartRate: pt.extensions?.hr || pt.extensions?.["gpxdata:hr"] || tpx(pt)?.["gpxtpx:hr"] || tpx(pt)?.["gpxdata:hr"],
    course:
      pt.extensions?.course ||
      pt.extensions?.["gpxdata:course"] ||
      tpx(pt)?.["gpxtpx:course"] ||
      tpx(pt)?.["gpxdata:course"],
    elevation: pt.elevation ?? 0,
    originalIndex: index
  }));

  // Apply speed interpolation and filtering
  const processedGpxPoints = interpolateAndFilterSpeedFromPoints(gpxPoints);

  const points = processedGpxPoints
    .map((pt: any, index: number) => {
      // Use course from GPX if available, otherwise calculate from GPS coordinates
      let bearing = -1;
      if (pt.course !== undefined && pt.course !== null && pt.course >= 0 && pt.course <= 360) {
        bearing = pt.course;
      } else if (
        index > 0 &&
        track.points[index - 1]?.latitude &&
        track.points[index - 1]?.longitude &&
        pt.latitude &&
        pt.longitude
      ) {
        bearing = DirectionUtil.getBearingValue(
          track.points[index - 1].latitude,
          track.points[index - 1].longitude,
          pt.latitude,
          pt.longitude
        );
      }

      return new Point({
        a: null,
        c: 0,
        h: pt.heartRate, // heart rate from extensions
        l: pt.latitude,
        n: pt.longitude,
        r: bearing,
        p: 0,
        // Use the calculated speed from the utility function
        s: pt.speed,
        e: pt.elevation, // elevation
        t: pt.timestamp,
        m: `point-${index}`
      });
    })
    .filter((point: any) => point);

  // Sort and enrich points
  const activity: any = new Activity({}, null);
  activity.points = points;
  activity.name = "";
  activity.typeId = ACTIVITY_DEFAULT_VALUES.typeId; // Default one
  activity.waterSportId = forcedWatersportId || 0; // Default one
  activity.feelingId = getFeelingId(ACTIVITY_DEFAULT_VALUES.feelingId); // Default one
  activity.fnt = "GPX";
  activity.startDateS = points?.[0]?.unixTimestamp || ACTIVITY_DEFAULT_VALUES.durationInUnixTimestamp;
  activity.originalDateInUTC = DateTimeUtil.getDateByTimeStamp(activity.startDateS);
  activity.pausedTimeInSeconds = 0;
  activity.durationInSeconds =
    points?.[points.length - 1]?.unixTimestamp - points?.[0]?.unixTimestamp ||
    ACTIVITY_DEFAULT_VALUES.durationInSeconds;
  activity.isDeleted = false;
  activity.isSaved = true;

  activity.bestSpeedFiveSecondsKM = calculateBestAvgSpeed(points, 5, 0, SETTING_SPEED_UNITS.km_slash_h)[0];
  const coords = activity.getCoordsFromPoints(activity.points);
  activity.linearDistance = CoordinateUtil.calculateLinearDistance(coords, SETTING_DISTANCE_UNITS.kilometers);
  activity.latitude = activity.points?.[0]?.latitude;
  activity.longitude = activity.points?.[0]?.longitude;
  activity.bearing = activity.points
    ? DirectionUtil.getBearing(
        activity.points[0].latitude,
        activity.points[0].longitude,
        activity.points[activity.points.length - 1].latitude,
        activity.points[activity.points.length - 1].longitude
      )
    : 0;

  // Check if we can use the point extension to get the last distance or use the track distance
  if (track.points[track.points.length - 1].extensions?.["gpxdata:distance"] || track.distance.total) {
    activity.totalDistanceInKm =
      (track.distance.total || track.points[track.points.length - 1].extensions?.["gpxdata:distance"]) / 1000;
  } else {
    const computedPoints = calculateTimeAndDistanceOfPoints(activity.points);
    activity.totalDistanceInKm = computedPoints[computedPoints.length - 1].distanceFromStartPointInMeter / 1000;
  }

  activity.averageSpeedInKM = activity.totalDistanceInKm / (activity.durationInSeconds / 3600);

  activity.maxSpeedInKM = ConvertUtil.convertMPSToKMH(
    activity.points.reduce((acc, point) => {
      acc = Math.max(acc, point.speedInMPS);
      return acc;
    }, 0)
  );

  const getLocAndWindData = async () => {
    const getWind = httpsCallableCustom(functions, "getWindByPlaceAndPeriod");
    const windInfo = await getWind({
      startDateMS: activity.startDateS,
      durationInSeconds: activity.durationInSeconds,
      point: {
        latitude: activity.latitude,
        longitude: activity.longitude
      }
    }).catch(() => {});
    const {windAngle, windDirection, windSpeedInKnots} = windInfo?.data || {};

    activity.windAngle = windAngle;
    activity.windDirection = windDirection;
    activity.windSpeedInKnots = windSpeedInKnots;
    await activity.getLocationInformation().catch(() => {});
  };

  if (isAndroid) {
    const status = await Network.getStatus();
    if (status.connected) {
      await getLocAndWindData();
    }
  } else if (!useCustomDomParser) {
    await getLocAndWindData();
  } else {
    if (activity && activity.points?.length > 0) {
      await activity.getWindInformation().catch((error) => {
        console.warn(`Error getting wind information for activity.`, error);
      });
      await activity.getLocationInformation().catch((error) => {
        console.warn(`Error getting location information for activity.`, error);
      });
    }
  }

  if (!useCustomDomParser) {
    activity.id = IdGeneratorUtil.generateUuid();
  }

  // Get the equipments corresponding to the sport type and assign the first three to the activity
  let equipments = await equipmentFactory.getAllEquipsByUser(
    userUid,
    null,
    {
      field: "sport",
      value: activity.waterSportId
    },
    3,
    false,
    undefined
  );
  equipments = equipments
    ? Object.values(equipments)?.filter((equipment: any) => equipment.sport === activity.waterSportId)
    : [];
  activity.equipment1 = equipments?.[0]?.sport || "";
  activity.equipment2 = equipments?.[1]?.sport || "";
  activity.equipment3 = equipments?.[2]?.sport || "";

  if (convert) {
    return activity.convertActivityToActivityDbAndPointsDb(userUid);
  }

  return activity;
};

export default createGPXFile;
